import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  UploadSimple,
  Download,
  AddressBook,
  CheckCircle,
  WarningCircle,
  XCircle,
  PencilSimple,
  ShieldCheck,
  Trash,
  Hourglass,
} from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

const PAGE_SIZE = 25;

export default function Recipients() {
  const { user: current } = useAuth();
  // Honor `?verification=pending|verified|all` so the Super Admin dashboard tile
  // can deep-link straight to the pending cohort.
  const initialVerification = (() => {
    if (typeof window === "undefined") return "all";
    const v = new URLSearchParams(window.location.search).get("verification");
    return ["pending", "verified", "all"].includes(v) ? v : "all";
  })();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [companies, setCompanies] = useState([]);
  const [filterCompany, setFilterCompany] = useState("all");
  // Verification filter: "all" | "pending" | "verified"
  const [filterVerification, setFilterVerification] = useState(initialVerification);
  const [loading, setLoading] = useState(false);

  // Import dialog state
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const csvRef = useRef(null);
  const jsonRef = useRef(null);

  // Recipient edit state — supports company_admin (direct) and dispatcher (override)
  const [editing, setEditing] = useState(null); // recipient row being edited or null
  // Delete confirmation
  const [deleting, setDeleting] = useState(null);
  const [deletingSaving, setDeletingSaving] = useState(false);
  // Tracks which row is currently mid-resend so we can disable the button + show "Sending…"
  const [resendingId, setResendingId] = useState(null);
  // Bulk-resend confirmation + state
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const isPlatform = current.role === "super_admin" || current.role === "warehouse_super";
  const canDelete = current.role === "super_admin" || current.role === "company_admin";
  // Roles that operate the platform (NOT viewers, customers, drivers) get the resend button.
  const canResendVerification = ["super_admin", "company_admin", "dispatcher", "warehouse", "warehouse_super"].includes(current.role);

  const resendVerification = async (row) => {
    if (!row?.recipient_email) return;
    setResendingId(row.id);
    try {
      await api.post("/auth/resend-verification", { email: row.recipient_email });
      toast.success(`Verification email re-sent to ${row.recipient_email}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not resend right now.");
    } finally {
      setResendingId(null);
    }
  };

  const bulkResendVerification = async () => {
    setBulkSending(true);
    try {
      const params = {};
      // Mirror the same company scope the table is currently showing.
      if (isPlatform && filterCompany !== "all") params.company_id = filterCompany;
      const { data } = await api.post("/recipients/resend-verification-bulk", null, { params });
      setBulkResult(data);
      toast.success(`Verification email sent to ${data.sent_count} unverified customer${data.sent_count === 1 ? "" : "s"}.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Bulk resend failed.");
    } finally {
      setBulkSending(false);
      setBulkConfirmOpen(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeletingSaving(true);
    try {
      await api.delete(`/recipients/${deleting.id}`);
      toast.success(`Removed ${deleting.recipient_name} from address book`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    } finally { setDeletingSaving(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (q) params.q = q;
      if (isPlatform && filterCompany !== "all") {
        params.company_id = filterCompany;
      }
      if (filterVerification !== "all") {
        params.verification = filterVerification;
      }
      const { data } = await api.get("/recipients", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isPlatform) {
      api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {});
    }
  }, [isPlatform]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterCompany, filterVerification]);

  const onSearch = (e) => {
    e.preventDefault();
    setPage(0);
    load();
  };

  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/recipients/template.csv", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "recipients_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download template");
    }
  };

  const doImport = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const params = {};
    if (isPlatform) {
      if (filterCompany === "all") {
        toast.error("Select a company first (filter at top)");
        return;
      }
      params.company_id = filterCompany;
    }
    try {
      const { data } = await api.post("/recipients/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params,
      });
      setImportResult(data);
      setImportOpen(true);
      toast.success(
        `Imported ${data.created} · skipped ${data.skipped}${data.total_rows ? ` of ${data.total_rows}` : ""}`
      );
      setPage(0);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally {
      if (csvRef.current) csvRef.current.value = "";
      if (jsonRef.current) jsonRef.current.value = "";
    }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]" data-testid="recipients-page">
      <Toaster position="top-right" />

      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
            Address Book
          </div>
          <h1
            className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1"
            style={{ fontFamily: "Work Sans" }}
          >
            Recipients
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Bulk-import customers via CSV or JSON. Each recipient gets a permanent customer number.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isPlatform && (
            <Select value={filterCompany} onValueChange={(v) => { setFilterCompany(v); setPage(0); }}>
              <SelectTrigger className="w-56" data-testid="recipients-company-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All companies</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant="outline"
            onClick={downloadTemplate}
            data-testid="download-template-btn"
            title="Download CSV template"
          >
            <Download size={15} className="mr-1.5" /> Template
          </Button>
          <input
            type="file"
            accept=".csv"
            ref={csvRef}
            className="hidden"
            onChange={(e) => e.target.files?.[0] && doImport(e.target.files[0])}
            data-testid="import-csv-input"
          />
          <Button
            variant="outline"
            onClick={() => csvRef.current?.click()}
            data-testid="import-csv-btn"
          >
            <UploadSimple size={15} className="mr-1.5" /> Import CSV
          </Button>
          <input
            type="file"
            accept=".json,application/json"
            ref={jsonRef}
            className="hidden"
            onChange={(e) => e.target.files?.[0] && doImport(e.target.files[0])}
            data-testid="import-json-input"
          />
          <Button
            className="bg-[#002BF6] hover:bg-[#0021C7] text-white"
            onClick={() => jsonRef.current?.click()}
            data-testid="import-json-btn"
          >
            <UploadSimple size={15} className="mr-1.5" /> Import JSON
          </Button>
        </div>
      </div>

      <form onSubmit={onSearch} className="mb-3 flex gap-2 max-w-md">
        <Input
          placeholder="Search name, email, phone, customer #"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="recipients-search"
        />
        <Button type="submit" variant="outline">Search</Button>
        {q && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => { setQ(""); setPage(0); setTimeout(load, 0); }}
          >
            Clear
          </Button>
        )}
      </form>

      {/* Verification filter chips + bulk resend (PRD: dispatch ops chase down unverified signups) */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mr-1">
          Verification
        </span>
        {[
          { value: "all", label: "All" },
          { value: "pending", label: "Pending" },
          { value: "verified", label: "Verified" },
        ].map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => { setFilterVerification(opt.value); setPage(0); }}
            data-testid={`verification-filter-${opt.value}`}
            className={
              "text-xs font-medium px-3 py-1 rounded-full border transition-colors " +
              (filterVerification === opt.value
                ? "bg-zinc-900 text-white border-zinc-900"
                : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400")
            }
          >
            {opt.label}
          </button>
        ))}
        {canResendVerification && filterVerification === "pending" && total > 0 && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setBulkConfirmOpen(true)}
            disabled={bulkSending}
            data-testid="bulk-resend-verification-btn"
            className="ml-2 text-amber-800 border-amber-300 hover:bg-amber-50"
          >
            <Hourglass size={13} className="mr-1.5" />
            {bulkSending ? "Resending…" : `Resend to all ${total} pending`}
          </Button>
        )}
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]" data-testid="recipients-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-5 py-3 font-semibold">Customer #</th>
              <th className="px-5 py-3 font-semibold">Name</th>
              <th className="px-5 py-3 font-semibold">Email</th>
              <th className="px-5 py-3 font-semibold">Verified</th>
              <th className="px-5 py-3 font-semibold">Phone</th>
              <th className="px-5 py-3 font-semibold">TRN #</th>
              <th className="px-5 py-3 font-semibold">Address</th>
              <th className="px-5 py-3 font-semibold">Created</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-5 py-12 text-center text-zinc-500">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-5 py-12 text-center text-zinc-500">
                  <AddressBook size={32} className="mx-auto mb-2 text-zinc-300" />
                  No recipients yet. Import a CSV or JSON to get started.
                </td>
              </tr>
            ) : (
              items.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50"
                  data-testid={`recipient-row-${r.id}`}
                >
                  <td className="px-5 py-3.5 font-mono text-xs text-zinc-700">
                    {r.customer_number || "—"}
                  </td>
                  <td className="px-5 py-3.5 font-medium text-zinc-900">
                    {r.recipient_name}
                  </td>
                  <td className="px-5 py-3.5 text-zinc-700">{r.recipient_email || "—"}</td>
                  <td className="px-5 py-3.5" data-testid={`recipient-verified-${r.id}`}>
                    {!r.has_account ? (
                      <span className="text-[10px] uppercase tracking-wider text-zinc-400">—</span>
                    ) : r.email_verified ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
                        <CheckCircle size={10} weight="fill" /> Verified
                      </span>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">
                          <Hourglass size={10} weight="fill" /> Pending
                        </span>
                        {canResendVerification && r.recipient_email && (
                          <button
                            type="button"
                            onClick={() => resendVerification(r)}
                            disabled={resendingId === r.id}
                            data-testid={`resend-verification-${r.id}`}
                            title={`Resend verification email to ${r.recipient_email}`}
                            className="text-[10px] uppercase tracking-wider font-semibold text-amber-800 hover:text-amber-900 underline underline-offset-2 disabled:opacity-50"
                          >
                            {resendingId === r.id ? "Sending…" : "Resend"}
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-zinc-700">{r.recipient_phone || "—"}</td>
                  <td className="px-5 py-3.5 text-zinc-700 font-mono text-xs" data-testid={`recipient-trn-${r.id}`}>
                    {r.trn ? (
                      <span title={r.trn_masked ? "Last 4 digits only — restricted by role" : ""}>
                        {r.trn}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-zinc-700 max-w-[280px] truncate" title={r.recipient_address || ""}>
                    {r.recipient_address || "—"}
                  </td>
                  <td className="px-5 py-3.5 text-zinc-500 text-xs whitespace-nowrap" data-testid={`recipient-created-${r.id}`}>
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditing(r)}
                      data-testid={`edit-recipient-${r.id}`}
                    >
                      <PencilSimple size={13} className="mr-1" /> Update
                    </Button>
                    {canDelete && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleting(r)}
                        data-testid={`delete-recipient-${r.id}`}
                        className="ml-1 text-red-600 hover:bg-red-50 hover:text-red-700"
                        title="Remove from address book"
                      >
                        <Trash size={13} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-zinc-600">
          <div data-testid="recipients-pagination-info">
            Showing {items.length ? page * PAGE_SIZE + 1 : 0}–{page * PAGE_SIZE + items.length} of {total}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage(0)}
              data-testid="recipients-first-btn"
              title="First page"
            >
              « First
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              data-testid="recipients-prev-btn"
            >
              Previous
            </Button>
            <span className="text-xs text-zinc-500 self-center px-1" data-testid="recipients-page-indicator">
              Page {page + 1} of {pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pages - 1}
              onClick={() => setPage((p) => p + 1)}
              data-testid="recipients-next-btn"
            >
              Next
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pages - 1}
              onClick={() => setPage(pages - 1)}
              data-testid="recipients-last-btn"
              title="Last page"
            >
              Last »
            </Button>
          </div>
        </div>
      )}

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent
          className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-2xl p-0 max-h-[85vh] gap-0 overflow-hidden flex flex-col"
          data-testid="import-result-dialog"
        >
          <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 border-b border-zinc-100 shrink-0">
            <DialogTitle>Import Results</DialogTitle>
          </DialogHeader>
          <div className="px-5 sm:px-6 py-4 overflow-y-auto flex-1">
            {importResult && (
              <>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <Stat label="Created" value={importResult.created} tone="success" />
                  <Stat label="Skipped" value={importResult.skipped} tone="warn" />
                  <Stat label="Total rows" value={importResult.total_rows} tone="neutral" />
                </div>
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">
                  Row-by-row
                </div>
                <div className="border border-zinc-200 rounded overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-zinc-50">
                      <tr className="text-left">
                        <th className="px-3 py-2">Row</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">Customer #</th>
                        <th className="px-3 py-2">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(importResult.results || []).map((r, i) => (
                        <tr key={i} className="border-t border-zinc-100">
                          <td className="px-3 py-2 font-mono">{r.row}</td>
                          <td className="px-3 py-2">
                            <StatusPill status={r.status} />
                          </td>
                          <td className="px-3 py-2">{r.recipient_name || "—"}</td>
                          <td className="px-3 py-2 font-mono">{r.customer_number || "—"}</td>
                          <td className="px-3 py-2 text-zinc-600">
                            {r.reason || r.recipient_email || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
          <DialogFooter className="px-5 sm:px-6 py-4 border-t border-zinc-100 shrink-0">
            <Button onClick={() => setImportOpen(false)} data-testid="close-result-dialog">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditRecipientDialog
        recipient={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
        currentUser={current}
      />

      {/* Delete-recipient confirmation */}
      <Dialog open={!!deleting} onOpenChange={(o) => { if (!o) setDeleting(null); }}>
        <DialogContent className="max-w-md" data-testid="delete-recipient-dialog">
          <DialogHeader>
            <DialogTitle>Remove recipient?</DialogTitle>
          </DialogHeader>
          {deleting && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-600 dark:text-zinc-300">
                You're about to remove <b className="text-zinc-900 dark:text-zinc-100">{deleting.recipient_name}</b>
                {deleting.recipient_email ? <> (<code className="text-xs">{deleting.recipient_email}</code>)</> : null} from the address book.
              </p>
              <p className="text-xs text-zinc-500">
                Their historical packages, invoices, and customer number stay intact. They can be re-added later via CSV import or by completing another delivery.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleting(null)} disabled={deletingSaving}>Cancel</Button>
            <Button
              onClick={confirmDelete}
              disabled={deletingSaving}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-recipient-btn"
            >
              {deletingSaving ? "Removing…" : (<><Trash size={14} className="mr-1.5" /> Remove</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk-resend verification confirmation */}
      <Dialog open={bulkConfirmOpen || !!bulkResult} onOpenChange={(o) => { if (!o) { setBulkConfirmOpen(false); setBulkResult(null); } }}>
        <DialogContent className="max-w-md" data-testid="bulk-resend-dialog">
          <DialogHeader>
            <DialogTitle>{bulkResult ? "Verification emails sent" : "Resend verification email to all pending?"}</DialogTitle>
          </DialogHeader>
          {bulkResult ? (
            <div className="space-y-2 text-sm">
              <p className="text-zinc-700">
                <b>{bulkResult.sent_count}</b> verification email{bulkResult.sent_count === 1 ? "" : "s"} dispatched.
                {bulkResult.failed_count > 0 && (
                  <> <b>{bulkResult.failed_count}</b> failed — check backend logs for details.</>
                )}
              </p>
              {(bulkResult.sent || []).length > 0 && (
                <div className="text-xs text-zinc-500 max-h-40 overflow-y-auto border border-zinc-100 rounded-md p-2 bg-zinc-50">
                  {bulkResult.sent.slice(0, 30).map((e) => <div key={e} className="font-mono">{e}</div>)}
                  {(bulkResult.sent.length > 30) && <div className="text-zinc-400 mt-1">…and {bulkResult.sent.length - 30} more</div>}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-zinc-600">
                Every unverified customer in <b>{isPlatform && filterCompany !== "all" ? (companies.find(c => c.id === filterCompany)?.name || "the selected company") : (isPlatform ? "all companies" : "your company")}</b> will receive a fresh verification email with a new 7-day link.
              </p>
              <p className="text-xs text-zinc-500">
                Existing verification links for those users will stop working — only the newly-sent link will verify their address.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            {bulkResult ? (
              <Button onClick={() => setBulkResult(null)} data-testid="bulk-resend-close-btn">Close</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setBulkConfirmOpen(false)} disabled={bulkSending}>Cancel</Button>
                <Button
                  onClick={bulkResendVerification}
                  disabled={bulkSending}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="confirm-bulk-resend-btn"
                >
                  {bulkSending ? "Sending…" : "Send to all"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditRecipientDialog({ recipient, onClose, onSaved, currentUser }) {
  const [form, setForm] = useState({ recipient_name: "", recipient_email: "", recipient_phone: "", recipient_address: "" });
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const requiresOverride = currentUser?.role === "dispatcher";

  useEffect(() => {
    if (recipient) {
      setForm({
        recipient_name: recipient.recipient_name || "",
        recipient_email: recipient.recipient_email || "",
        recipient_phone: recipient.recipient_phone || "",
        recipient_address: recipient.recipient_address || "",
      });
      setAdminEmail("");
      setAdminPassword("");
    }
  }, [recipient]);

  if (!recipient) return null;

  const save = async () => {
    setSaving(true);
    try {
      if (requiresOverride) {
        if (!adminEmail.trim() || !adminPassword) {
          toast.error("Enter a company admin email and password to authorise this change");
          setSaving(false);
          return;
        }
        await api.post(`/recipients/${recipient.id}/update-with-override`, {
          admin_email: adminEmail.trim(),
          admin_password: adminPassword,
          updates: form,
        });
        toast.success("Recipient updated (authorised by admin)");
      } else {
        await api.patch(`/recipients/${recipient.id}`, form);
        toast.success("Recipient updated");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!recipient} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-2xl p-0 max-h-[90vh] gap-0 overflow-hidden flex flex-col" data-testid="edit-recipient-dialog">
        <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 border-b border-zinc-100 shrink-0">
          <DialogTitle>Update recipient — {recipient.recipient_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 overflow-y-auto px-5 sm:px-6 py-4 flex-1">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Full name</Label>
            <Input className="mt-1.5" value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} data-testid="edit-recipient-name" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Email</Label>
              <Input type="email" className="mt-1.5" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} data-testid="edit-recipient-email" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Phone</Label>
              <Input className="mt-1.5" value={form.recipient_phone} onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })} data-testid="edit-recipient-phone" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Home address</Label>
            <AddressAutocomplete
              value={form.recipient_address}
              onChange={(v) => setForm({ ...form, recipient_address: v })}
              rows={2}
              className="mt-1.5"
              dataTestId="edit-recipient-address"
            />
          </div>

          {requiresOverride && (
            <div className="mt-4 border border-amber-200 bg-amber-50/50 rounded-md p-4" data-testid="override-block">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck size={15} weight="bold" className="text-amber-600" />
                <span className="text-sm font-semibold text-amber-900">
                  Company-admin authorisation required
                </span>
              </div>
              <p className="text-xs text-amber-800 mb-3">
                Dispatchers cannot modify recipient records directly. A company admin from your company must enter their credentials below to authorise this change. Both users will be recorded in the audit log.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-amber-900">Admin email</Label>
                  <Input className="mt-1.5" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="admin@company.com" data-testid="override-admin-email" autoComplete="off" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-amber-900">Admin password</Label>
                  <Input type="password" className="mt-1.5" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} data-testid="override-admin-password" autoComplete="off" />
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="px-5 sm:px-6 py-4 border-t border-zinc-100 bg-white shrink-0 gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="save-recipient-btn">
            {saving ? "Saving…" : requiresOverride ? "Authorise & save" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, tone }) {
  const colors = {
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    warn: "bg-amber-50 text-amber-700 border-amber-200",
    neutral: "bg-zinc-50 text-zinc-700 border-zinc-200",
  }[tone];
  return (
    <div className={`border rounded p-3 ${colors}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">
        {label}
      </div>
      <div className="text-2xl font-semibold mt-0.5">{value ?? 0}</div>
    </div>
  );
}

function StatusPill({ status }) {
  if (status === "created") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
        <CheckCircle size={11} weight="bold" /> Created
      </span>
    );
  }
  if (status === "skipped") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
        <WarningCircle size={11} weight="bold" /> Skipped
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
      <XCircle size={11} weight="bold" /> {status}
    </span>
  );
}
